import React, { useContext, useEffect, useState } from 'react';

import { v4 as uuidv4 } from 'uuid';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Typography } from '@mui/material';

import { apiEndPoints } from '../../common/api.endPoints';
import { Loading } from '../../common/components/Loading';
import { HttpService } from '../../common/http.service';
import '../detail/tasks/components/autoMedicalClaims/AutoMedicalClaims.scss';
import { AutoMedicalClaimsRuleHitsPanel } from '../detail/tasks/components/autoMedicalClaims/AutoMedicalClaimsRuleHitsPanel';
import { fetchAutoMedicalClaims } from '../detail/tasks/components/autoMedicalClaims/fetchAutoMedicalClaims';
import {
  MedicalClaim,
  RuleHit,
} from '../detail/tasks/components/autoMedicalClaims/types';
import { factsExist } from '../detail/utilities/taskDetailUtilities';
import { SnackBarType } from '../layout/interfaces/ISnacksProps';
import { SnackbarContext } from '../layout/SnackbarContext';
import { AutoMedicalClaimsData } from '../tasks/types/autoMedicalClaims';
import { Evidence } from '../tasks/types/evidence';
import { TaskTypes } from '../tasks/types/taskTypes';
import { fetchEvidenceData } from './helpers';

export const AutoMedicalClaimsDetail = ({
  product,
  carrier,
  policyId,
  evidenceId,
}: {
  product: string;
  carrier: string;
  policyId: string;
  evidenceId: number;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [evidence, setEvidence] = useState<Evidence | null>(null);
  const [evidenceInprogress, setEvidenceInprogress] = useState(false);
  const [evidenceReady, setEvidenceReady] = useState(false);
  const [loadingMagicLink, setLoadingMagicLink] = useState(false);
  const [ruleHits, setRuleHits] = useState<RuleHit[]>([]);
  const [claimsById, setClaimsById] = useState<Record<number, MedicalClaim>>(
    {},
  );
  const [independentClaims, setIndependentClaims] = useState<MedicalClaim[]>(
    [],
  );
  const { openSnacks, setSnacks } = useContext(SnackbarContext);

  const facts: AutoMedicalClaimsData = evidence?.facts as AutoMedicalClaimsData;
  const errorMessage = evidence?.errorMessage;
  const hasHit = facts?.recordExists ?? false;
  const hasClear = facts?.recordClear ?? false;

  const showError = (message: string) => {
    setSnacks([
      ...openSnacks,
      {
        id: uuidv4(),
        type: SnackBarType.ALERT,
        message,
      },
    ]);
  };

  useEffect(() => {
    if (
      !evidenceId ||
      !evidenceReady ||
      errorMessage ||
      !(hasHit || hasClear)
    ) {
      return;
    }

    let cancelled = false;
    fetchAutoMedicalClaims(evidenceId)
      .then(result => {
        if (cancelled) return;
        setRuleHits(result.ruleHits);
        setClaimsById(result.claimsById);
        setIndependentClaims(result.independentClaims);
      })
      .catch(() => {
        if (!cancelled) {
          showError('Failed to load auto medical claims rule data');
        }
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evidenceId, evidenceReady, hasHit, hasClear, errorMessage]);

  const doExpand = () => {
    (async () => {
      if (!expanded) {
        if (!evidenceReady && !evidenceInprogress) {
          setEvidenceInprogress(true);
          const result = await fetchEvidenceData(
            product,
            carrier,
            policyId,
            evidenceId.toString(),
            TaskTypes.AUTO_MEDICAL_CLAIMS,
          );
          setEvidence(result);
          setEvidenceReady(true);
          setEvidenceInprogress(false);
        }
      }
      setExpanded(!expanded);
    })();
  };

  const onOpenMillimanPortal = async () => {
    setLoadingMagicLink(true);
    try {
      const response = await HttpService.getInstance().get(
        `${apiEndPoints.millimanPortal}?evidenceId=${evidenceId}`,
      );
      const url = await response?.text();
      if (url?.length > 0) {
        window.open(url, '_blank');
      } else {
        showError('Can not open milliman portal link, please try again later');
      }
    } catch (error) {
      console.error(error);
      showError('Can not open milliman portal link, please try again later');
    } finally {
      setLoadingMagicLink(false);
    }
  };

  return (
    <Box pt={8}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        ml={-1}
      >
        <Box display="flex" flexDirection="row">
          <Box mt={-0.15} ml={-3.5} position="absolute">
            <IconButton
              style={{ width: '24px' }}
              size="small"
              onClick={doExpand}
            >
              <FontAwesomeIcon
                icon={faAngleRight}
                rotation={expanded ? 90 : undefined}
              />
            </IconButton>
          </Box>
          <Typography variant="h2">Auto Medical Claims</Typography>
        </Box>
      </Box>

      {expanded &&
        (evidenceInprogress ? (
          <Loading fullscreen={false} />
        ) : (
          evidenceReady &&
          (evidence ? (
            factsExist(evidence) &&
            !errorMessage && (
              <AutoMedicalClaimsRuleHitsPanel
                ruleHits={ruleHits}
                claimsById={claimsById}
                independentClaims={independentClaims}
                hasHit={hasHit}
                hasClear={hasClear}
                loadingMagicLink={loadingMagicLink}
                onOpenMillimanPortal={onOpenMillimanPortal}
              />
            )
          ) : (
            <Box>
              <Typography variant="body1">
                failed to load auto medical claims data
              </Typography>
            </Box>
          ))
        ))}
    </Box>
  );
};
